import qs from "qs";
import React, { useEffect, useState } from "react";
import { TailSpin } from "react-loader-spinner";
import { useNavigate } from "react-router-dom";
import api from "../api";
import { useFilters } from "./FilterContext";

interface ProblemRoute {
  routeId: number;
  routeName: string;
  eventCount: number;
  distanceMiles: number;
  eventsPerMile: number;
}

type SortColumn = keyof ProblemRoute;

const ProblemRoutes: React.FC = () => {
  const [routes, setRoutes] = useState<ProblemRoute[]>([]);
  const [sortColumn, setSortColumn] = useState<SortColumn>("eventsPerMile");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const [loading, setIsLoading] = useState(true);

  // For navigation on row click
  const navigate = useNavigate();
  const { filters } = useFilters();


  // Fetch data on mount
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const { data: res } = await api.get("/dashboard/problematic-routes", {
          params: {
            routeIds: filters.routeIds, eventTypes: filters.eventTypes, ...(filters.startDate && { startDate: filters.startDate }),
            ...(filters.endDate && { endDate: filters.endDate })
          },
          paramsSerializer: (params) =>
            qs.stringify(params, { arrayFormat: "comma" }),
        });
        // Convert string fields to numbers
        const problemRoutes: ProblemRoute[] = res.map((route: any) => ({
          routeId: Number(route.routeId),
          routeName: route.routeName,
          eventCount: Number(route.eventCount),
          distanceMiles: Number(route.distanceMiles),
          eventsPerMile: Number(route.eventsPerMile),
        }));

        // Initial sort (optional if you want to ensure default is eventsPerMile desc)
        problemRoutes.sort((a, b) => b.eventsPerMile - a.eventsPerMile);

        setRoutes(problemRoutes);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [filters]);

  // Sort logic
  const sortedRoutes = [...routes].sort((a, b) => {
    const valA = a[sortColumn];
    const valB = b[sortColumn];

    // If comparing strings
    if (typeof valA === "string" && typeof valB === "string") {
      const compare = valA.localeCompare(valB);
      return sortDirection === "asc" ? compare : -compare;
    }

    // Otherwise, compare as numbers
    if (typeof valA === "number" && typeof valB === "number") {
      return sortDirection === "asc" ? valA - valB : valB - valA;
    }

    return 0;
  });

  // Toggle sorting on column header click
  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  // Display sort arrows
  const renderSortArrow = (column: SortColumn) => {
    if (sortColumn === column) {
      return sortDirection === "asc" ? "▲" : "▼";
    }
    return "";
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6 w-full h-[450px]">
      {/* Chart Title */}
      <h3 className="text-left text-2xl font-bold text-gray-800 mb-2">
        Problematic Routes
      </h3>

      {loading ? (
        <div className="flex items-center justify-center w-full h-[300px]">
          <TailSpin
            height={80}
            width={80}
            color="#2563eb"
            strokeWidth={2}
            ariaLabel="oval-loading"
            visible={true}
          />
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-md overflow-hidden mt-6">
          <table className="w-full border-collapse table-fixed text-sm">
            <thead className="bg-blue-600 text-white">
              <tr>
                {/* ID/Name */}
                <th
                  className="p-3 text-left w-1/5 cursor-pointer"
                  onClick={() => handleSort("routeName")}
                >
                  Route {renderSortArrow("routeName")}
                </th>

                {/* Distance */}
                <th
                  className="p-3 text-left w-1/5 cursor-pointer"
                  onClick={() => handleSort("distanceMiles")}
                >
                  Distance (mi) {renderSortArrow("distanceMiles")}
                </th>

                {/* Event Count */}
                <th
                  className="p-3 text-left w-1/5 cursor-pointer"
                  onClick={() => handleSort("eventCount")}
                >
                  Event Count {renderSortArrow("eventCount")}
                </th>

                {/* Events Per Mile */}
                <th
                  className=" text-left w-1/5 cursor-pointer"
                  onClick={() => handleSort("eventsPerMile")}
                >
                  Events Per Mile {renderSortArrow("eventsPerMile")}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {sortedRoutes.map((route) => (
                <tr
                  key={route.routeId}
                  className="hover:bg-gray-100 transition-colors duration-200 cursor-pointer"
                  onClick={() => navigate(`/route/details/${route.routeId}`)}
                >
                  <td className="p-3">{route.routeName}</td>
                  <td className="p-3">{route.distanceMiles.toFixed(2)}</td>
                  <td className="p-3">{route.eventCount}</td>
                  <td className="p-3">{route.eventsPerMile.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ProblemRoutes;
